import React from 'react';
import { publicAddressApi } from '../../lib/api';

export interface AddressSuggestion {
    label: string;
    address: string;
    postcode: string;
    city: string;
}

interface Props {
    value: string;
    onChange: (v: string) => void;
    onSelect: (s: AddressSuggestion) => void;
    placeholder?: string;
    className?: string;
}

// Recherche d'adresse en direct (débounce 400ms, 3 caractères min) sur le champ "Adresse" des surfaces
// publiques de réservation — via la BAN (api-adresse.data.gouv.fr), gratuite et sans clé. `onSelect`
// renvoie les champs déjà structurés (numéro+rue / code postal / ville) pour préremplir les champs séparés
// correspondants, sans que l'utilisateur ait à les ressaisir.
export const AddressAutocompleteInput: React.FC<Props> = ({ value, onChange, onSelect, placeholder, className }) => {
    const [suggestions, setSuggestions] = React.useState<AddressSuggestion[]>([]);
    const [open, setOpen] = React.useState(false);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleChange = (v: string) => {
        onChange(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (v.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
        debounceRef.current = setTimeout(() => {
            publicAddressApi.autocomplete(v.trim()).then((data: any) => {
                setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
                setOpen(true);
            }).catch(() => {});
        }, 400);
    };

    const pick = (s: AddressSuggestion) => {
        onChange(s.address || s.label);
        onSelect(s);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div className="relative">
            <input value={value} onChange={(e) => handleChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder={placeholder} className={className} autoComplete="off" />
            {open && suggestions.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-surface border border-border rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                    {suggestions.map((s, i) => (
                        <button key={i} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
                            className="w-full text-left px-3 py-2 text-xs text-content hover:bg-surface-soft transition-colors border-b border-border last:border-b-0">
                            {s.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
